import React, { useState, useRef } from 'react';
import { FiSearch, FiPlus, FiX, FiUpload, FiCheckSquare } from 'react-icons/fi';
import '../../components/dashboard/components/tables.css';
import { Button, Modal, TextField, Select, Combobox, ComboboxHandle, DeleteConfirmModal } from '../../design-system';
import { listAllProjectSamples, listWells, addSample, updateSample, deleteSample, addWell } from '../../api/data';
import { Sample, CalculationStrategies } from '../../@types/samples/types';
import { analysesWithResult } from '../MultiAnalysisPage/analysisCategory';
import { Well } from '../../@types/wells/types';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import SampleCard from './SampleCard';
import { ManageWellsModal } from './ManageWellsModal';
import ScalExcelImportModal from './ScalExcelImportModal';
import { getApiErrorMessage } from '../../utils';
import { FieldErrors, toNumberOrNull, validateNewSample } from './sampleFormValidation';
import styles from './CapillaryPressurePage.module.css';

type IconProps = { size?: number; color?: string };
const SearchIcon = FiSearch as React.FC<IconProps>;
const PlusIcon = FiPlus as React.FC<IconProps>;
const XIcon = FiX as React.FC<IconProps>;
const UploadIcon = FiUpload as React.FC<IconProps>;
const MultiSelectIcon = FiCheckSquare as React.FC<IconProps>;

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (sample: Sample, allSamples: Sample[], well?: Well) => void;
  onSelectMany?: (samples: Sample[], allSamples: Sample[]) => void;
  projectUid: string | null;
};

const EMPTY_FORM = { sampleCode: '', wellUid: '', depth: '', depthUnit: 'ft' as 'ft' | 'm', permeability: '', porosity: '', coreSampleLength: '', swi: '' };
const EMPTY_NEW_WELL = {
  name: '',
  color: '#4ADE80',
};


const LoadSampleModal = ({ open, onClose, projectUid, onSelect, onSelectMany }: Props) => {
  const { selectedInfo } = useSelected();
  const { data, updateData, updateConfigStates } = useAppContext();

  const [newSampleOpen, setNewSampleOpen] = useState(false);
  const comboboxRef = useRef<ComboboxHandle>(null);

  // list state
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name_asc' | 'name_desc' | 'depth_asc' | 'depth_desc'>('name_asc');
  const [wellFilter, setWellFilter] = useState('');
  const [analysisFilter, setAnalysisFilter] = useState<'' | CalculationStrategies>('');

  // new-sample state
  const [importOpen, setImportOpen] = useState(false);
  const [wells, setWells] = useState<Well[]>([]);
  const [wellsLoading, setWellsLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [creating, setCreating] = useState(false);

  // new-well state
  const [isCreatingNewWell, setIsCreatingNewWell] = useState(false);
  const [newWellData, setNewWellData] = useState(EMPTY_NEW_WELL);

  // edit state
  const [editTarget, setEditTarget] = useState<Sample | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // remove state
  const [removing, setRemoving] = useState<string | null>(null);
  const [deleteTargets, setDeleteTargets] = useState<Sample[]>([]);

  // edit sample state
  const [isEditingSample, setIsEditingSample] = useState(false);

  // edit well state
  const [isEditWellModalOpen, setIsEditWellModalOpen] = useState(false);

  // multi-select state
  const multiSelectEnabled = Boolean(onSelectMany);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedUids, setSelectedUids] = useState<Set<string>>(() => new Set());

  const resolveWell = React.useCallback(
    (wellUid: string) =>
      wells.find(w => w.uid === wellUid) ||
      data.wells.find(w => w.uid === wellUid) ||
      (selectedInfo.well?.uid === wellUid ? selectedInfo.well : undefined),
    [wells, data.wells, selectedInfo.well]
  );

  const handleEditSample = (sample: Sample) => {
    setEditTarget(sample);
    setForm({
      sampleCode: sample.sample_code,
      wellUid: sample.well_uid,
      depth: sample.depth != null ? String(sample.depth) : '',
      depthUnit: sample.depth_unit || 'ft',
      permeability: '',
      porosity: '',
      coreSampleLength: '',
      swi: '',
    });
    setIsEditingSample(true);
  };

  React.useEffect(() => {
    if (!open || fetched || !projectUid) return;

    const load = async () => {
      setLoading(true);
      try {
        const [samplesData, wellsData] = await Promise.all([
          listAllProjectSamples(projectUid),
          listWells(projectUid),
        ]);
        setSamples(Array.isArray(samplesData) ? samplesData : []);
        const normalizedWells = Array.isArray(wellsData) ? wellsData : [];
        setWells(normalizedWells);
        updateData({ wells: normalizedWells });
        setFetched(true);
      } catch (err) {
        console.error('Error loading samples:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, fetched, projectUid, updateData]);

  // Invalidate the cached project-scoped lists when the project changes so the
  // load effect re-fetches. Without this the `fetched` guard keeps showing the
  // previous project's samples/wells after a client-side project switch.
  React.useEffect(() => {
    setFetched(false);
    setSamples([]);
    setWells([]);
    setSelectedUids(new Set());
    setSearch('');
    setWellFilter('');
    setAnalysisFilter('');
  }, [projectUid]);

  React.useEffect(() => {
    if (!open) {
      setNewSampleOpen(false);
      setForm(EMPTY_FORM);
      setFormError(null);
      setFieldErrors({});
      setEditTarget(null);
      setEditError(null);
      setDeleteTargets([]);
      setWellFilter('');
      setAnalysisFilter('');
      setImportOpen(false);
      setMultiSelectMode(false);
      setSelectedUids(new Set());
    }
  }, [open]);

  const toggleSelected = (uid: string) => {
    setSelectedUids(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const handleLoadSelected = () => {
    if (!onSelectMany) return;
    const picked = samples.filter(s => selectedUids.has(s.uid));
    if (picked.length === 0) return;
    onSelectMany(picked, samples);
  };

  const exitMultiSelect = () => {
    setMultiSelectMode(false);
    setSelectedUids(new Set());
  };

  const openNewSample = async () => {
    setNewSampleOpen(true);
    if (wells.length > 0 || !projectUid) return;
    setWellsLoading(true);
    try {
      const data = await listWells(projectUid);
      const normalizedWells = Array.isArray(data) ? data : [];
      setWells(normalizedWells);
      updateData({ wells: normalizedWells });
    } catch {
      setWells([]);
    } finally {
      setWellsLoading(false);
    }
  };

  const closeNewSample = () => {
    setNewSampleOpen(false);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFieldErrors({});
    setIsCreatingNewWell(false);
    setNewWellData(EMPTY_NEW_WELL);
  };

  const handleCreate = async () => {
    if (!form.sampleCode.trim()) {
      setFormError('Sample code is required.');
      return;
    }
    if (!form.wellUid && !isCreatingNewWell) {
      setFormError('Please select a well.');
      return;
    }
    if (!projectUid) return;

    const errors = validateNewSample(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError('Please fix the highlighted fields before creating the sample.');
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setCreating(true);
    try {
      let wellUid = form.wellUid;
      if (isCreatingNewWell) {
        const newWell = await addWell({
          project_uid: projectUid,
          well_name: newWellData.name,
          color: newWellData.color,
          curves: [],
        });

        wellUid = newWell.uid;
      }

      const created = await addSample({
        project_uid: projectUid,
        well_uid: wellUid,
        sample_code: form.sampleCode.trim(),
        depth: form.depth !== '' ? Number(form.depth) : null,
        depth_unit: form.depthUnit,
        sample_length: toNumberOrNull(form.coreSampleLength),
        input_sample_values: {
          permeability: toNumberOrNull(form.permeability),
          porosity: toNumberOrNull(form.porosity),
          swi: toNumberOrNull(form.swi),
        },
        input_sample_curves: {},
      });

      const [samplesData, wellsData] = await Promise.all([
        listAllProjectSamples(projectUid),
        listWells(projectUid),
      ]);
      const all = Array.isArray(samplesData) ? samplesData : [];
      setSamples(all);
      const normalizedWells = Array.isArray(wellsData) ? wellsData : [];
      setWells(normalizedWells);
      updateData({ wells: normalizedWells });
      setFetched(true);
      onSelect(created, all, normalizedWells.find(w => w.uid === created.well_uid));
    } catch (err) {
      setFormError(getApiErrorMessage(err, 'Failed to create sample. Please try again.'));
    } finally {
      setCreating(false);
    }
  };

  const closeEdit = () => {
    setEditTarget(null);
    setIsEditingSample(false);
    setForm(EMPTY_FORM);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    if (!form.sampleCode.trim()) {
      setEditError('Sample code is required.');
      return;
    }
    setEditError(null);
    setSaving(true);
    try {
      await updateSample({
        sample_uid: editTarget.uid,
        sample_code: form.sampleCode.trim(),
        well_uid: form.wellUid,
        depth: form.depth !== '' ? Number(form.depth) : null,
        depth_unit: form.depthUnit,
      });
      if (projectUid) {
        const data = await listAllProjectSamples(projectUid);
        setSamples(Array.isArray(data) ? data : []);
      }
      closeEdit();
    } catch (err) {
      setEditError(getApiErrorMessage(err, 'Failed to save changes. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = (sample: Sample) => {
    setDeleteTargets([sample]);
  };

  const handleDeleteSelected = () => {
    const picked = samples.filter(s => selectedUids.has(s.uid));
    if (picked.length === 0) return;
    setDeleteTargets(picked);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargets.length === 0) return;
    const targets = deleteTargets;
    const targetUids = new Set(targets.map(sample => sample.uid));
    setRemoving(targets.length === 1 ? targets[0].uid : 'bulk');
    try {
      await Promise.all(targets.map(sample => deleteSample(sample.uid)));
      setSamples(prev => prev.filter(s => !targetUids.has(s.uid)));
      setSelectedUids(prev => {
        const next = new Set(prev);
        targetUids.forEach(uid => next.delete(uid));
        return next;
      });
      setDeleteTargets([]);
    } catch (err) {
      console.error('Failed to remove sample:', err);
    } finally {
      setRemoving(null);
    }
  };

  const handleImportedSamples = async (savedSamples: Sample[], targetWellUid: string) => {
    if (!projectUid) return;
    const [samplesData, wellsData] = await Promise.all([
      listAllProjectSamples(projectUid),
      listWells(projectUid),
    ]);
    const all = Array.isArray(samplesData) ? samplesData : [];
    const normalizedWells = Array.isArray(wellsData) ? wellsData : [];
    const targetWell = normalizedWells.find(well => well.uid === targetWellUid);
    setSamples(all);
    setWells(normalizedWells);
    updateData({
      wells: normalizedWells,
      wellSamples: all.filter(sample => sample.well_uid === targetWellUid),
    });
    updateConfigStates({ fetchSamples: true });
    setFetched(true);

    const firstSaved = savedSamples[0];
    if (firstSaved) {
      const fresh = all.find(sample => sample.uid === firstSaved.uid) ?? firstSaved;
      setImportOpen(false);
      onSelect(fresh, all, targetWell);
    }
  };

  const filtered = samples
    .filter(s => !search || (s.sample_code ?? '').toLowerCase().includes(search.toLowerCase()))
    .filter(s => !wellFilter || s.well_uid === wellFilter)
    .filter(s => !analysisFilter || analysesWithResult(s).includes(analysisFilter))
    .sort((a, b) => {
      if (sortBy === 'name_asc') return (a.sample_code ?? '').localeCompare(b.sample_code ?? '');
      if (sortBy === 'name_desc') return (b.sample_code ?? '').localeCompare(a.sample_code ?? '');
      if (sortBy === 'depth_asc') return (a.depth ?? 0) - (b.depth ?? 0);
      if (sortBy === 'depth_desc') return (b.depth ?? 0) - (a.depth ?? 0);
      return 0;
    });

  const wellOptions = wells.map(w => ({ value: w.uid, label: w.well_name }));
  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Load Sample"
        size="lg"
        footer={
          multiSelectMode ? (
            <>
              <Button variant="secondary" onClick={exitMultiSelect}>Cancel</Button>
              <Button
                variant="primary"
                className={styles.multiSelectDeleteButton}
                disabled={selectedUids.size === 0}
                onClick={handleDeleteSelected}
              >
                Remove {selectedUids.size > 0 ? selectedUids.size : ''} {selectedUids.size === 1 ? 'sample' : 'samples'}
              </Button>
              <Button
                variant="primary"
                disabled={selectedUids.size === 0}
                onClick={handleLoadSelected}
              >
                Load {selectedUids.size > 0 ? selectedUids.size : ''} {selectedUids.size === 1 ? 'sample' : 'samples'}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          )
        }
      >
        <div className={styles.modalContent}>
          <div className={styles.modalToolbar}>
            <div className={styles.searchRow}>
              <div className={styles.searchField}>
                <TextField
                  placeholder="Search by sample code…"
                  fullWidth
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  startAdornment={<SearchIcon size={15} color="#A7B7AF" />}
                />
              </div>
              {multiSelectEnabled && (
                <Button
                  variant={multiSelectMode ? 'primary' : 'secondary'}
                  startIcon={<MultiSelectIcon />}
                  onClick={() => (multiSelectMode ? exitMultiSelect() : setMultiSelectMode(true))}
                >
                  {multiSelectMode ? 'Done' : 'Select multiple'}
                </Button>
              )}
              <Button variant="secondary" startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
                Batch Import
              </Button>
              <Button variant="primary" startIcon={<PlusIcon />} onClick={openNewSample}>
                New Sample
              </Button>
            </div>

            <div className={styles.filtersRow}>
              <div className={styles.filterCell}>
                <Select
                  placeholder="All Wells"
                  value={wellFilter || undefined}
                  onChange={val => setWellFilter((val as string) ?? '')}
                  options={[
                    { value: '', label: 'All Wells' },
                    ...wells.map(w => ({ value: w.uid, label: w.well_name })),
                  ]}
                  footerAction={{
                    label: 'Manage Wells',
                    onClick: () => setIsEditWellModalOpen(true),
                  }}
                />
              </div>
              <div className={styles.filterCell}>
                <Select
                  placeholder="All Analyses"
                  value={analysisFilter || undefined}
                  onChange={val => setAnalysisFilter((val as CalculationStrategies | '') ?? '')}
                  options={[
                    { value: '', label: 'All Analyses' },
                    { value: CalculationStrategies.LIAO, label: 'Liao' },
                    { value: CalculationStrategies.LAMBDA, label: 'Brooks Corey' },
                    { value: CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY, label: 'Water-Oil' },
                    { value: CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY, label: 'Gas-Oil' },
                  ]}
                />
              </div>
              <div className={styles.filterCell}>
                <Select
                  value={sortBy}
                  onChange={val => setSortBy(val as any)}
                  options={[
                    { value: 'name_asc', label: 'Alphabetical (A–Z)' },
                    { value: 'name_desc', label: 'Alphabetical (Z–A)' },
                    { value: 'depth_asc', label: 'Depth (Low to High)' },
                    { value: 'depth_desc', label: 'Depth (High to Low)' },
                  ]}
                />
              </div>
            </div>
          </div>

          {multiSelectMode && (
            <div className={styles.multiSelectToolbar}>
              <span>
                {selectedUids.size === 0
                  ? 'Pick samples to load them all at once.'
                  : `${selectedUids.size} sample${selectedUids.size === 1 ? '' : 's'} selected`}
              </span>
              <div className={styles.multiSelectActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const all = new Set(filtered.map(s => s.uid));
                    setSelectedUids(all);
                  }}
                  disabled={filtered.length === 0}
                >
                  Select all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedUids(new Set())}
                  disabled={selectedUids.size === 0}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          {loading && (
            <div className={styles.modalLoading}>
              <span className={styles.spinner} />
              Loading samples…
            </div>
          )}

          {!loading && !projectUid && (
            <p className={styles.modalEmpty}>
              No project selected. Open the dashboard and select a project first.
            </p>
          )}

          {!loading && projectUid && filtered.length === 0 && (
            <p className={styles.modalEmpty}>
              {search || wellFilter || analysisFilter ? 'No samples match your filters.' : 'No samples found for this project.'}
            </p>
          )}

          {!loading && filtered.length > 0 && (
            <div className={styles.sampleGrid}>
              {filtered.map(sample => (
                <SampleCard
                  key={sample.uid}
                  sample={sample}
                  onClick={
                    multiSelectMode
                      ? () => toggleSelected(sample.uid)
                      : () => onSelect(sample, samples, resolveWell(sample.well_uid))
                  }
                  onEdit={handleEditSample}
                  onRemove={handleRemove}
                  removing={removing === sample.uid}
                  selectable={multiSelectMode}
                  selected={selectedUids.has(sample.uid)}
                />
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* New Sample modal */}
      <Modal
        open={newSampleOpen}
        onClose={closeNewSample}
        title="New Sample"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={closeNewSample}>Back</Button>
            <Button variant="primary" loading={creating} onClick={handleCreate}>
              Create Sample
            </Button>
          </>
        }
      >
        <div className={styles.newSampleForm}>
          <div className={styles.newSampleField}>
            <label className={styles.newSampleLabel}>Sample Code *</label>
            <TextField
              placeholder="e.g. C1-H121"
              fullWidth
              value={form.sampleCode}
              onChange={e => setForm(f => ({ ...f, sampleCode: e.target.value }))}
            />
          </div>

          <div className={styles.newSampleField}>
            <label className={styles.newSampleLabel}>Well *</label>
            {wellsLoading ? (
              <div className={styles.modalLoading}>
                <span className={styles.spinner} /> Loading wells…
              </div>
            ) : (
              <div className={styles.comboboxWrapper}>
                <Combobox
                  ref={comboboxRef}
                  placeholder="Select or create a well"
                  fullWidth
                  value={isCreatingNewWell ? 'NEW_WELL' : form.wellUid}
                  disabled={isCreatingNewWell}
                  onChange={val => {
                    setForm(f => ({ ...f, wellUid: val }));
                    setIsCreatingNewWell(false);
                  }}
                  onCreate={name => {
                    setNewWellData(prev => ({ ...prev, name }));
                    setIsCreatingNewWell(true);
                    setForm(f => ({ ...f, wellUid: '' }));
                  }}
                  options={isCreatingNewWell
                    ? [...wellOptions, { value: 'NEW_WELL', label: newWellData.name }]
                    : wellOptions
                  }
                />
                <Button
                  variant="secondary"
                  onClick={() => {
                    setNewWellData(EMPTY_NEW_WELL);
                    setIsCreatingNewWell(true);
                    setForm(f => ({ ...f, wellUid: '' }));
                  }}
                  disabled={isCreatingNewWell}
                  title="Create new well"
                >
                  <PlusIcon size={16} />
                </Button>
              </div>
            )}
          </div>

          {isCreatingNewWell && (
            <div className={styles.newWellSubForm}>
              <div className={styles.newWellSubFormHeader}>
                <span className={styles.newWellSubFormTitle}>New Well Details</span>
                <button
                  type="button"
                  className={styles.newWellSubFormCancel}
                  onClick={() => {
                    setIsCreatingNewWell(false);
                    setNewWellData(EMPTY_NEW_WELL);
                  }}
                  title="Cancel new well creation"
                >
                  <XIcon size={18} />
                </button>
              </div>

              <div className={styles.newSampleField}>
                <label className={styles.newSampleLabel}>Well Name</label>
                <TextField
                  fullWidth
                  value={newWellData.name}
                  onChange={e => setNewWellData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className={styles.newSampleRow}>
                <div className={styles.newSampleField}>
                  <label className={styles.newSampleLabel}>Color</label>
                  <div className={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      className={styles.colorPicker}
                      value={newWellData.color}
                      onChange={e => setNewWellData(prev => ({ ...prev, color: e.target.value }))}
                    />
                    <span className={styles.colorValue}>{newWellData.color}</span>
                  </div>
                </div>
              </div>

            </div>
          )}

          <div className={styles.newSampleRow}>
            <div className={styles.newSampleField}>
              <label className={styles.newSampleLabel}>Depth *</label>
              <TextField
                placeholder="e.g. 1623.5"
                fullWidth
                value={form.depth}
                status={fieldErrors.depth ? 'error' : 'default'}
                hint={fieldErrors.depth ?? 'Required — must be greater than 0'}
                onChange={e => {
                  const depth = e.target.value.replace(/[^0-9.]/g, '');
                  setForm(f => ({ ...f, depth }));
                  setFieldErrors(prev => ({ ...prev, depth: undefined }));
                }}
              />
            </div>
            <div className={styles.newSampleFieldNarrow}>
              <label className={styles.newSampleLabel}>Unit</label>
              <Select
                fullWidth
                value={form.depthUnit}
                onChange={val => setForm(f => ({ ...f, depthUnit: val as 'ft' | 'm' }))}
                options={[
                  { value: 'ft', label: 'ft' },
                  { value: 'm', label: 'm' },
                ]}
              />
            </div>
          </div>

          <div className={styles.newSampleRow}>
            <div className={styles.newSampleField}>
              <label className={styles.newSampleLabel}>Permeability (mD)</label>
              <TextField
                placeholder="e.g. 0.5"
                fullWidth
                value={form.permeability}
                status={fieldErrors.permeability ? 'error' : 'default'}
                hint={fieldErrors.permeability ?? 'Must be ≥ 0'}
                onChange={e => {
                  const permeability = e.target.value.replace(/[^0-9.]/g, '');
                  setForm(f => ({ ...f, permeability }));
                  setFieldErrors(prev => ({ ...prev, permeability: undefined }));
                }}
              />
            </div>
            <div className={styles.newSampleField}>
              <label className={styles.newSampleLabel}>Porosity (fraction)</label>
              <TextField
                placeholder="e.g. 0.25"
                fullWidth
                value={form.porosity}
                status={fieldErrors.porosity ? 'error' : 'default'}
                hint={fieldErrors.porosity ?? 'Between 0 and 1 (1 not allowed)'}
                onChange={e => {
                  const porosity = e.target.value.replace(/[^0-9.]/g, '');
                  setForm(f => ({ ...f, porosity }));
                  setFieldErrors(prev => ({ ...prev, porosity: undefined }));
                }}
              />
            </div>
          </div>

          <div className={styles.newSampleRow}>
            <div className={styles.newSampleField}>
              <label className={styles.newSampleLabel}>Core Sample Length</label>
              <TextField
                placeholder="e.g. 5.2"
                fullWidth
                value={form.coreSampleLength}
                onChange={e => setForm(f => ({ ...f, coreSampleLength: e.target.value.replace(/[^0-9.]/g, '') }))}
              />
            </div>
            <div className={styles.newSampleField}>
              <label className={styles.newSampleLabel}>Swi</label>
              <TextField
                placeholder="e.g. 0.15"
                fullWidth
                value={form.swi}
                hint="Fraction 0–1. If not provided, min(Sw) from curves is used"
                onChange={e => setForm(f => ({ ...f, swi: e.target.value.replace(/[^0-9.]/g, '') }))}
              />
            </div>
          </div>

          {formError && <p className={styles.newSampleError}>{formError}</p>}
        </div>
      </Modal>

      {/* Edit Sample modal */}
      <Modal
        open={isEditingSample}
        onClose={closeEdit}
        title="Edit Sample"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={closeEdit}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSaveEdit}>
              Save
            </Button>
          </>
        }
      >
        <div className={styles.newSampleForm}>
          <div className={styles.newSampleField}>
            <label className={styles.newSampleLabel}>Sample Code *</label>
            <TextField
              placeholder="e.g. C1-H121"
              fullWidth
              value={form.sampleCode}
              onChange={e => setForm(f => ({ ...f, sampleCode: e.target.value }))}
            />
          </div>

          <div className={styles.newSampleField}>
            <label className={styles.newSampleLabel}>Well *</label>
            <div className={styles.comboboxWrapper}>
              <Combobox
                ref={comboboxRef}
                placeholder="Select a well"
                fullWidth
                value={form.wellUid}
                onChange={val => setForm(f => ({ ...f, wellUid: val }))}
                options={wellOptions}
              />
              <Button
                variant="secondary"
                onClick={() => comboboxRef.current?.focus()}
                title="Open well selection"
              >
                <PlusIcon size={16} />
              </Button>
            </div>
          </div>

          <div className={styles.newSampleRow}>
            <div className={styles.newSampleField}>
              <label className={styles.newSampleLabel}>Depth</label>
              <TextField
                placeholder="e.g. 1623.5"
                fullWidth
                value={form.depth}
                onChange={e => setForm(f => ({ ...f, depth: e.target.value.replace(/[^0-9.]/g, '') }))}
              />
            </div>
            <div className={styles.newSampleFieldNarrow}>
              <label className={styles.newSampleLabel}>Unit</label>
              <Select
                fullWidth
                value={form.depthUnit}
                onChange={val => setForm(f => ({ ...f, depthUnit: val as 'ft' | 'm' }))}
                options={[
                  { value: 'ft', label: 'ft' },
                  { value: 'm', label: 'm' },
                ]}
              />
            </div>
          </div>

          {editError && <p className={styles.newSampleError}>{editError}</p>}
        </div>
      </Modal>
      <ManageWellsModal
        open={isEditWellModalOpen}
        onClose={() => setIsEditWellModalOpen(false)}
        wells={wells}
        projectUid={projectUid}
        onWellsChange={setWells}
      />
      <ScalExcelImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        projectUid={projectUid}
        wells={wells}
        defaultWellUid={selectedInfo.well?.uid}
        onWellsChange={setWells}
        onImported={handleImportedSamples}
      />
      <DeleteConfirmModal
        open={deleteTargets.length > 0}
        onClose={() => setDeleteTargets([])}
        onConfirm={handleConfirmDelete}
        itemType={deleteTargets.length === 1 ? 'sample' : 'samples'}
        itemName={
          deleteTargets.length === 1
            ? deleteTargets[0].sample_code || 'sample'
            : `${deleteTargets.length} selected samples`
        }
        loading={removing !== null}
      />
    </>
  );
};

export default LoadSampleModal;
